/* eslint-disable no-undef, camelcase, prefer-destructuring */

/**
 * Utility function to convert address_components from Google Maps API into an address string
 * @function
 * @param {Array} addressComponents - address_components array from a single results node
 * @returns {String} eg: 123 Main St, Vancouver, BC
 */
export const googleAddressStringFromParts = (addressComponents) => {
  const address = [];

  if (Array.isArray(addressComponents)) {
    const addressParts = [
      "street_number",
      "route",
      "locality",
      "administrative_area_level_2",
      "administrative_area_level_1",
    ];
    // transform the array of address component objects into a single new object
    // using 'type' as keys
    const addressObj = addressComponents.reduce((acc, curr) => {
      addressParts.some((e) => {
        if (curr.types && curr.types.includes(e)) {
          acc[e] = curr;
        }
      });

      return acc;
    }, {});

    const {
      street_number,
      route,
      locality,
      administrative_area_level_2,
      administrative_area_level_1,
    } = addressObj;

    if (route) {
      if (street_number) {
        address.push(`${street_number.short_name} ${route.short_name}`);
      } else {
        address.push(route.short_name);
      }
    }

    if (locality) {
      address.push(locality.short_name);
    } else if (administrative_area_level_2) {
      address.push(administrative_area_level_2.short_name);
    }

    if (administrative_area_level_1) {
      address.push(administrative_area_level_1.short_name);
    }
  }

  return address.join(", ");
};

/**
 * Fetch a geocoded address from the Google Maps JS API by latitude and longitude
 * @function
 * @param {Number} lat - latitude
 * @param {Number} lon - longitude
 * @returns {Promise}
 */
export const geocodeCoordinates = (lat, lng) =>
  new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.google) {
      const geocoder = new google.maps.Geocoder();
      const latLng = new google.maps.LatLng(lat, lng);
      geocoder.geocode({ latLng }, (results, status) => {
        if (status === "OK") {
          // eslint-disable-next-line camelcase
          const { address_components = [] } = results?.[0] ?? {};
          resolve(googleAddressStringFromParts(address_components));
        }
        reject(status);
      });
    }
  });

/**
 * Fetch a geocoded address from the Google Maps JS API by address and region
 * @function
 * @param {String} searchQuery - address
 * @param {String} countryCode - two letter country code
 * @returns {Promise} eg: 123 Main St, Vancouver, BC
 */
export const geocodeAddress = (searchQuery, countryCode) =>
  new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.google) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: searchQuery, region: countryCode },
        (results, status) => {
          const { address_components = [], geometry = {} } = results?.[0] ?? {};
          if (status === "OK") {
            resolve({
              lat: geometry?.location?.lat(),
              lng: geometry?.location?.lng(),
              address: googleAddressStringFromParts(address_components),
            });
          }
          reject(status);
        }
      );
    }
  });
